import { ChangeDetectionStrategy, Component, inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { OrganizationProfile } from 'app/organization/model/organization-profile';
import { ClientSystemLinksService } from 'app/organization/client-system-links.service';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IAppConfig } from 'app/app-config';
import { ClientSystemLink } from 'app/organization/model/client-system-link';
import { AsyncPipe } from '@angular/common';
import { SvgIconDirective } from '@styleguide/svg-icon/svg-icon.directive';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { ProfileImageComponent } from '@styleguide/profile/profile-image/profile-image.component';
import { BadgeCounterComponent } from '@customer-apps/badge/badge-counter/badge-counter.component';
import { ApiImageUrlPipe } from '@customer-apps/api-image/api-image-url.pipe';
import { BADGE_KEY_INVOICE } from '@customer-apps/accounting/invoice';
import { AccountingService } from '@customer-apps/accounting/accounting.service';
import { OrganizationService } from '@customer-apps/organization/organization.service';
import { UserService } from '@customer-apps/user/user.service';
import { BaseUser } from '@customer-apps/user/models/user';
import { AuthenticationService } from '@customer-apps/user/authentication.service';
import { UserCredentialsStore } from '@customer-apps/user/user-credentials-store.service';
import { ConfigService } from '@customer-apps/config/config.service';
import { BadgeService } from '@customer-apps/badge/badge.service';
import { IfModuleEnabledDirective } from '@customer-apps/enabled-modules/if-module-enabled.directive';
import { MenuLanguageSwitchComponent } from '@customer-apps/menu-language-switch/menu-language-switch.component';
import { AppStateService } from '@customer-apps/app-state/app-state.service';

@Component({
  selector: 'kgr-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgIconDirective,
    RouterLinkActive,
    RouterLink,
    ProfileImageComponent,
    BadgeCounterComponent,
    AsyncPipe,
    ApiImageUrlPipe,
    IfModuleEnabledDirective,
    MenuLanguageSwitchComponent,
  ],
})
export class UserMenuComponent implements OnInit {
  private readonly appStateService = inject(AppStateService);
  private readonly activeLocaleId = inject(LOCALE_ID);

  readonly BADGE_KEY_INVOICE = BADGE_KEY_INVOICE;

  @Input({ required: true }) user: BaseUser | null = null;

  organization$ = this.organizationService.organization$;
  users$ = this.userService.users$;
  hasMultipleAccounts$ = this.authenticationService.hasMultipleAccounts$;

  clientSystemLinks$: Observable<ClientSystemLink[]> = this.userCredentialsStore.userCredentials$.pipe(
    switchMap((userCredentials) => {
      if (userCredentials) {
        return this.clientSystemLinksService.getAuthenticatedClientSystemLinks();
      } else {
        return of([]);
      }
    }),
  );

  usersDropdownOpen = true;

  privacyUrl = this.configService.config.privacyUrl;
  staticLogo = this.configService.config.staticLogo;
  clientLogoFileName = this.configService.config.clientLogoInMenuFileName || this.configService.config.clientLogoFileName;

  constructor(
    private organizationService: OrganizationService<OrganizationProfile>,
    private userService: UserService<BaseUser>,
    private authenticationService: AuthenticationService,
    private userCredentialsStore: UserCredentialsStore,
    private clientSystemLinksService: ClientSystemLinksService,
    private configService: ConfigService<IAppConfig>,
    private accountingService: AccountingService,
    private badgeService: BadgeService,
  ) {}

  ngOnInit() {
    this.organization$
      .pipe(
        switchMap((organization) => {
          if (organization?.membersCanViewInvoices) {
            return this.accountingService.getInvoices();
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((invoices) => {
        this.badgeService.setBadgeCounter(BADGE_KEY_INVOICE, this.accountingService.countUnread(invoices));
      });
  }

  toggleUserDropdown() {
    this.usersDropdownOpen = !this.usersDropdownOpen;
  }

  setLocaleId(localeId: string) {
    const currentUrl = document.location.href;
    let targetUrl: string;

    if (this.appStateService.isNative) {
      targetUrl = currentUrl.replace(new RegExp(`/${this.activeLocaleId}/index.html`), `/${localeId}/index.html`);
    } else {
      targetUrl = currentUrl.replace(/\/app\/\w{2}\//, `/app/${localeId}/`);
    }

    document.location.href = targetUrl;
  }
}
